import html2canvas from 'html2canvas';

export interface ScreenshotOptions {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  format?: 'png' | 'jpeg';
  redactSelectors?: string[];
}

export interface ScreenshotResult {
  blob: Blob;
  width: number;
  height: number;
  format: string;
  capturedAt: string;
}

const DEFAULT_REDACT_SELECTORS = [
  'input[type="password"]',
  '[data-redact]',
];

export async function captureScreenshot(
  options: ScreenshotOptions = {}
): Promise<ScreenshotResult> {
  const {
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1080,
    format = 'png',
    redactSelectors = DEFAULT_REDACT_SELECTORS,
  } = options;

  const allSelectors = [...new Set([...DEFAULT_REDACT_SELECTORS, ...redactSelectors])];
  const redactedElements: { el: HTMLElement; original: string }[] = [];

  for (const selector of allSelectors) {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    elements.forEach((el) => {
      redactedElements.push({ el, original: el.style.visibility });
      el.style.visibility = 'hidden';
    });
  }

  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      allowTaint: false,
      scale: 1,
    });

    const resized = resizeCanvas(canvas, maxWidth, maxHeight);

    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await canvasToBlob(resized, mimeType, quality);

    return {
      blob,
      width: resized.width,
      height: resized.height,
      format,
      capturedAt: new Date().toISOString(),
    };
  } finally {
    for (const { el, original } of redactedElements) {
      el.style.visibility = original;
    }
  }
}

function resizeCanvas(
  source: HTMLCanvasElement,
  maxWidth: number,
  maxHeight: number
): HTMLCanvasElement {
  const { width, height } = source;

  if (width <= maxWidth && height <= maxHeight) {
    return source;
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  const newWidth = Math.round(width * ratio);
  const newHeight = Math.round(height * ratio);

  const resized = document.createElement('canvas');
  resized.width = newWidth;
  resized.height = newHeight;

  const ctx = resized.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas 2d context');

  ctx.drawImage(source, 0, 0, newWidth, newHeight);
  return resized;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert canvas to blob'));
      },
      mimeType,
      quality
    );
  });
}
